<template>
  <div
    v-if="visible"
    class="floating-window"
    :style="windowStyle"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click.stop
  >
    <!-- 顶部栏 - 包含图片和标题 -->
    <div class="window-header">
      <!-- 图片区域 - 放在标题前面 -->
      <div v-if="imageUrl" class="image-container">
        <img :src="imageUrl" :alt="imageAlt" class="window-image" />
      </div>
      <h3 class="window-title">{{ title }}</h3>
      <button class="close-btn" @click="handleClose">×</button>
    </div>
    
    <!-- 内容区域 -->
    <div class="window-content">
      <!-- 文字内容 -->
      <div class="content-text" v-html="content"></div>
      
      <!-- 操作按钮 -->
      <div v-if="showButton" class="button-container">
        <button class="action-btn" @click="handleAction">
          {{ buttonText }}
        </button>
      </div>
    </div>
    
    <!-- 装饰元素 -->
    <div class="window-decoration"></div>
  </div>
</template>

<script>
export default {
  name: 'FloatingWindow',
  props: {
    content: {
      type: String,
      default: '这是一个悬浮飘动的窗口'
    },
    title: {
      type: String,
      default: '提示信息'
    },
    imageUrl: {
      type: String,
      default: ''
    },
    imageAlt: {
      type: String,
      default: '提示图片'
    },
    showButton: {
      type: Boolean,
      default: false
    },
    buttonText: {
      type: String,
      default: '了解更多'
    },
    speedX: {
      type: Number,
      default: 1.2
    },
    speedY: {
      type: Number,
      default: 0.9
    },
    width: {
      type: Number,
      default: 280
    },
    height: {
      type: Number,
      default: 240
    },
    color: {
      type: String,
      default: '#333333'
    },
    backgroundColor: {
      type: String,
      default: 'rgba(255, 255, 255, 0.95)'
    },
    borderColor: {
      type: String,
      default: '#e6a23c'
    },
    borderRadius: {
      type: Number,
      default: 12
    }
  },
  data() {
    return {
      visible: true,
      x: 0,
      y: 0,
      dx: this.speedX,
      dy: this.speedY,
      isPaused: false,
      animationFrameId: null,
      // 添加随机初始角度用于更自然的飘动
      rotation: Math.random() * 2 - 1,
      rotationSpeed: (Math.random() * 0.2 - 0.1) / 2,
      // 平滑过渡相关
      targetRotation: 0,
      easeFactor: 0.1
    };
  },
  computed: {
    windowStyle() {
      return {
        position: 'fixed',
        left: `${this.x}px`,
        top: `${this.y}px`,
        width: `${this.width}px`,
        height: `${this.height}px`,
        backgroundColor: this.backgroundColor,
        color: this.color,
        borderRadius: `${this.borderRadius}px`,
        border: `2px solid ${this.borderColor}`,
        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.15)',
        cursor: 'pointer',
        zIndex: 1000,
        userSelect: 'none',
        display: 'flex',
        flexDirection: 'column',
        transform: `rotate(${this.rotation}deg)`,
        overflow: 'hidden',
        // 硬件加速
        willChange: 'transform'
      };
    }
  },
  mounted() {
    // 初始化位置为页面随机位置
    this.x = Math.random() * (window.innerWidth - this.width);
    this.y = Math.random() * (window.innerHeight - this.height);
    this.startFloat();
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    this.stopFloat();
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    startFloat() {
      this.stopFloat(); // 防止重复启动
      
      const animate = () => {
        if (!this.isPaused) {
          this.updatePosition();
          this.updateRotation();
        }
        // 继续下一帧
        this.animationFrameId = requestAnimationFrame(animate);
      };
      
      // 启动动画
      animate();
    },
    stopFloat() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    },
    updatePosition() {
      // 更新位置
      this.x += this.dx;
      this.y += this.dy;
      
      // 检测边界并平滑反弹
      const maxX = window.innerWidth - this.width;
      const maxY = window.innerHeight - this.height;
      const bouncePadding = 5; // 添加一些内边距，让反弹更平滑
      
      // X轴边界检测
      if (this.x <= bouncePadding) {
        // 平滑反弹
        if (this.dx < 0) {
          this.x = bouncePadding;
          this.dx = Math.abs(this.dx) * 0.8; // 轻微减速
          // 反弹时平滑调整速度
          this.smoothRandomizeMovement();
        }
      } else if (this.x >= maxX - bouncePadding) {
        if (this.dx > 0) {
          this.x = maxX - bouncePadding;
          this.dx = -Math.abs(this.dx) * 0.8;
          this.smoothRandomizeMovement();
        }
      }
      
      // Y轴边界检测
      if (this.y <= bouncePadding) {
        if (this.dy < 0) {
          this.y = bouncePadding;
          this.dy = Math.abs(this.dy) * 0.8;
          this.smoothRandomizeMovement();
        }
      } else if (this.y >= maxY - bouncePadding) {
        if (this.dy > 0) {
          this.y = maxY - bouncePadding;
          this.dy = -Math.abs(this.dy) * 0.8;
          this.smoothRandomizeMovement();
        }
      }
    },
    updateRotation() {
      // 更新旋转角度，使其有轻微的摇摆效果
      this.rotation += this.rotationSpeed;
      // 限制旋转角度范围
      if (this.rotation > 1) {
        this.rotation = 1;
        this.rotationSpeed = -Math.abs(this.rotationSpeed);
      } else if (this.rotation < -1) {
        this.rotation = -1;
        this.rotationSpeed = Math.abs(this.rotationSpeed);
      }
    },
    smoothRandomizeMovement() {
      // 平滑调整速度，避免突然变化
      const currentDx = this.dx;
      const currentDy = this.dy;
      
      // 目标速度
      const targetDx = currentDx > 0 ? 
        Math.min(Math.max(this.speedX * (0.8 + Math.random() * 0.4), 0.8), 2) : 
        -Math.min(Math.max(this.speedX * (0.8 + Math.random() * 0.4), 0.8), 2);
      
      const targetDy = currentDy > 0 ? 
        Math.min(Math.max(this.speedY * (0.8 + Math.random() * 0.4), 0.6), 1.8) : 
        -Math.min(Math.max(this.speedY * (0.8 + Math.random() * 0.4), 0.6), 1.8);
      
      // 使用缓动效果平滑过渡
      this.dx += (targetDx - currentDx) * this.easeFactor;
      this.dy += (targetDy - currentDy) * this.easeFactor;
      
      // 平滑调整旋转速度
      const targetRotationSpeed = (Math.random() * 0.16 - 0.08) / 2;
      this.rotationSpeed += (targetRotationSpeed - this.rotationSpeed) * this.easeFactor;
    },
    handleMouseEnter() {
      // 鼠标悬停时平滑停止
      this.isPaused = true;
      // 重置旋转角度，使窗口回到水平状态
      this.targetRotation = 0;
      this.smoothResetRotation();
    },
    handleMouseLeave() {
      // 延迟恢复移动，避免快速进出导致的闪烁
      setTimeout(() => {
        if (!this.isPaused) return;
        this.isPaused = false;
        // 恢复自然的旋转
        this.rotationSpeed = (Math.random() * 0.2 - 0.1) / 2;
      }, 300);
    },
    smoothResetRotation() {
      // 平滑重置旋转角度
      const animateReset = () => {
        if (Math.abs(this.rotation) > 0.05) {
          this.rotation += (this.targetRotation - this.rotation) * 0.2;
          requestAnimationFrame(animateReset);
        } else {
          this.rotation = 0;
        }
      };
      animateReset();
    },
    handleClose() {
      this.visible = false;
      this.stopFloat();
      this.$emit('close');
    },
    handleAction() {
      this.$emit('action');
    },
    handleResize() {
      // 确保窗口在可视区域内
      const maxX = window.innerWidth - this.width;
      const maxY = window.innerHeight - this.height;
      this.x = Math.min(Math.max(this.x, 0), maxX);
      this.y = Math.min(Math.max(this.y, 0), maxY);
    }
  }
};
</script>

<style scoped>
.floating-window {
  position: fixed;
  border-radius: var(--border-radius, 12px);
  background: var(--background-color, rgba(255, 255, 255, 0.95));
  border: 1px solid var(--border-color, #e6a23c);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  --color: #333333;
  --border-color: #e6a23c;
  --border-radius: 12px;
  /* 硬件加速 */
  will-change: transform;
  backface-visibility: hidden;
  perspective: 1000px;
}

/* 移除CSS动画，避免与JavaScript动画冲突 */

.floating-window:hover {
  transform: scale(1.03) rotate(0deg);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

/* 顶部栏样式 - 确保图片和标题在同一行 */
.window-header {
  display: flex;
  align-items: center;
  padding: 12px 16px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: linear-gradient(to right, var(--border-color, #e6a23c), transparent);
}

/* 图片区域样式 - 标题前面的图片 */
.image-container {
  margin-right: 8px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.window-image {
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
  transition: transform 0.3s ease;
}

.floating-window:hover .window-image {
  transform: scale(1.1);
}

.window-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color, #333333);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.close-btn {
  background: rgba(0, 0, 0, 0.1);
  border: none;
  font-size: 20px;
  color: var(--color, #333333);
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  margin-left: 8px;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.2);
  transform: scale(1.1);
}

.window-content {
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.content-text {
  font-size: 14px;
  line-height: 1.6;
  color: var(--color, #333333);
  flex: 1;
  overflow-wrap: break-word;
}

.button-container {
  margin-top: 12px;
  display: flex;
  justify-content: center;
}

.action-btn {
  background-color: var(--border-color, #e6a23c);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;
}

.action-btn:hover {
  background-color: #d49436;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(230, 162, 60, 0.3);
}

/* 滚动条样式 */
.window-content::-webkit-scrollbar {
  width: 6px;
}

.window-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}

.window-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.window-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}
</style>